<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import moment from "moment";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import Swal from "sweetalert2";
import { extractTipo, etiquetaTipo } from "@/services/usuarios";
import { cargarIndiceSuscripciones, resolverEstadoMembresia } from "@/services/suscripciones";
import { descargarCsv } from "@/services/csv";
import { registrar, ACCIONES } from "@/services/bitacora";
import { empleadoComputed } from "@/state/helpers";

export default {
    page: {
        title: "Inicio",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: {
        Layout,
        PageHeader,
    },
    data() {
        return {
            title: "Inicio",
            users_active: [],
            users_inactive: [],
            users_all: [],
            companies_active: [],
            companies_inactive: [],
            userSelected: null,
            showUser: false,
            filtro: "Todos los usuarios",
            filtro_referidos: "Activos",
            items: [
                { text: "Inicio", href: "/" },
                { text: "Home", active: true },
            ],
            referral_export_users: [],
            // Índice { correo: {active, current_period_end, ...} } del padrón de Stripe.
            indiceSuscripciones: {},
            searchQuery: "",
            showDownloadModal: false,
            downloadOption: "todos",
            currentPage: 1,
            perPage: 10,
            isEditing: false,
            editableUser: {},
            referredByName: '',
            activeReferrals: [],
            inactiveReferrals: [],
            referralFields: [
                { key: 'name', label: 'Nombre' },
                { key: 'phone', label: 'Teléfono' },
                { key: 'creation_formatted', label: 'Fecha de Registro' },
            ],
        };
    },
    computed: {
        ...empleadoComputed,
        filteredUsers() {
            let users = [];
            if (this.filtro === "Todos los usuarios") {
                users = this.users_all;
            } else if (this.filtro === "Usuarios Activos") {
                users = this.users_active;
            } else if (this.filtro === "Usuarios Inactivos") {
                users = this.users_inactive;
            }
            if (!this.searchQuery) return users;
            const q = this.searchQuery.toLowerCase();
            return users.filter(
                u =>
                    (u.name && u.name.toLowerCase().includes(q)) ||
                    (u.email && u.email.toLowerCase().includes(q))
            );
        },
        paginatedUsers() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredUsers.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredUsers.length / this.perPage) || 1;
        },
        isSubscriptionActive() {
            if (!this.userSelected) return { text: 'N/A', variant: 'secondary' };
            return this.tieneSuscripcion(this.userSelected.email)
                ? { text: 'Si', variant: 'success' }
                : { text: 'No', variant: 'danger' };
        },
        userType() {
            // La app escribe "normal" o "comercio" (ver UserForm/index.js). Comparar
            // contra 'user' hacía que todo usuario normal apareciera como "Empresa".
            if (!this.userSelected) return '';
            return etiquetaTipo(extractTipo(this.userSelected));
        },
        documentList() {
            if (!this.userSelected) return [];
            return [
                { label: 'INE', url: this.userSelected.ine },
                { label: 'Cédula Fiscal', url: this.userSelected.cedula },
                { label: 'Estado de cuenta', url: this.userSelected.estado_cuenta },
                // CAMBIO: Se quitó el 'Contrato' de esta lista
                { label: 'Banco', url: null, value: this.userSelected.banco },
                { label: 'Cuenta/Tarjeta', url: null, value: this.userSelected.clabe },
                { label: 'RFC', url: null, value: this.userSelected.rfc },
            ];
        },
        filteredReferrals() {
            return this.filtro_referidos === 'Activos' ? this.activeReferrals : this.inactiveReferrals;
        }
    },
    watch: {
        filteredUsers() {
            if ((this.currentPage - 1) * this.perPage >= this.filteredUsers.length) {
                this.currentPage = 1;
            }
        }
    },
    mounted() {
        this.fetchUsersWithSubscriptions().then(() => {
            firebase
                .firestore()
                .collection("app_usuarios")
                .orderBy("creation_timestamp", "desc")
                .get()
                .then((querySnapshot) => {
                    var array = [];
                    let index = 0;
                    querySnapshot.forEach((documentSnapshot) => {
                        array.push(documentSnapshot.data());
                        array[index].id = documentSnapshot.id;
                        array[index].creation_formatted = moment(
                            documentSnapshot.data().creation_timestamp,
                            "x"
                        ).format("DD-MM-YYYY");
                        array[index].active = this.tieneSuscripcion(
                            documentSnapshot.data().email
                        );
                        index++;
                    });
                    this.users_active = array.filter((item) => item.active == true);
                    this.users_inactive = array.filter((item) => item.active == false);
                    this.users_all = array;
                });
        });
    },
    methods: {
        openDownloadModal() {
            this.showDownloadModal = true;
            this.downloadOption = "todos";
        },
        handleDownload() {
            let data = [];
            if (this.downloadOption === "todos") {
                data = this.users_all;
            } else if (this.downloadOption === "activos") {
                data = this.users_active;
            } else if (this.downloadOption === "inactivos") {
                data = this.users_inactive;
            }
            this.download_csv(data);
            this.showDownloadModal = false;
        },
        download_csv(data) {
            // El CSV anterior pegaba los valores con comas sin escapar: un nombre o
            // una ciudad con coma desalineaba las columnas. Además exportaba
            // `referral_users_quantity`, un campo que la app nunca escribe.
            descargarCsv(
                "usuarios",
                [
                    { key: "id", label: "Id" },
                    { key: "email", label: "Email" },
                    { key: "name", label: "Nombre" },
                    { key: "phone", label: "Telefono" },
                    { key: "city", label: "Ciudad" },
                    { key: "creation_formatted", label: "Fecha de registro" },
                    { key: "referral_code", label: "Código de referido" },
                    { key: "referred_by", label: "Referido por" },
                    {
                        key: "active",
                        label: "Suscripción activa",
                        formato: (v) => (v ? "Si" : "No"),
                    },
                ],
                data
            );
        },

        verPerfil(data) {
            this.userSelected = data;
            this.showUser = true;
            this.filtro_referidos = "Activos";
            this.activeReferrals = [];
            this.inactiveReferrals = [];
            this.referredByName = 'Cargando...';

            firebase.firestore().collection("app_usuarios")
                .where("referred_by", "==", data.referral_code).get()
                .then((querySnapshot) => {
                    const referredUsers = querySnapshot.docs.map(doc => {
                        const userData = doc.data();
                        return {
                            ...userData,
                            id: doc.id,
                            creation_formatted: moment(userData.creation_timestamp, "x").format("YYYY-MM-DD"),
                            active: this.tieneSuscripcion(userData.email)
                        };
                    });
                    this.activeReferrals = referredUsers.filter(u => u.active);
                    this.inactiveReferrals = referredUsers.filter(u => !u.active);
                });

            if (data.referred_by) {
                firebase.firestore().collection("app_usuarios")
                    .where("referral_code", "==", data.referred_by).get()
                    .then((querySnapshot) => {
                        if (!querySnapshot.empty) {
                            this.referredByName = querySnapshot.docs[0].data().name;
                        } else {
                            this.referredByName = "Código no encontrado";
                        }
                    });
            } else {
                this.referredByName = 'N/A';
            }
        },

        async fetchUsersWithSubscriptions() {
            try {
                this.indiceSuscripciones = await cargarIndiceSuscripciones();
            } catch (error) {
                console.error("Error al cargar suscripciones:", error.response || error);
                Swal.fire(
                    "Suscripciones no disponibles",
                    "No se pudieron cargar las suscripciones de Stripe. La lista se muestra sin ese dato.",
                    "warning"
                );
            }
        },

        /** Membresía vigente según el índice de Stripe. */
        tieneSuscripcion(email) {
            if (!email) return false;
            const entrada = this.indiceSuscripciones[String(email).toLowerCase().trim()];
            return resolverEstadoMembresia(entrada).activo;
        },

        activarEdicion() {
            this.editableUser = {
                id: this.userSelected.id,
                name: this.userSelected.name,
                phone: this.userSelected.phone,
                city: this.userSelected.city,
                referred_by: this.userSelected.referred_by,
            };
            this.isEditing = true;
        },
        cancelarEdicion() {
            this.isEditing = false;
            this.editableUser = {};
        },
        // CAMBIO: Lógica de guardado y actualización de página
        async guardarCambios() {
            if (!this.editableUser || !this.editableUser.id) {
                Swal.fire("Error", "No se pudo identificar el usuario.", "error");
                return;
            }

            const updates = {
                name: this.editableUser.name,
                phone: this.editableUser.phone,
                city: this.editableUser.city,
                referred_by: this.editableUser.referred_by,
            };

            const antes = {
                name: this.userSelected.name,
                phone: this.userSelected.phone,
                city: this.userSelected.city,
                referred_by: this.userSelected.referred_by,
            };

            try {
                await firebase.firestore().collection("app_usuarios").doc(this.editableUser.id).update(updates);
                await registrar({
                    accion: ACCIONES.EDITAR,
                    coleccion: "app_usuarios",
                    docId: this.editableUser.id,
                    descripcion: this.userSelected.email,
                    antes,
                    despues: updates,
                });

                Swal.fire({
                    title: "Éxito",
                    text: "Usuario actualizado correctamente.",
                    icon: "success",
                    confirmButtonText: "De acuerdo"
                }).then((result) => {
                    if (result.isConfirmed) {
                        location.reload(); // Recarga la página
                    }
                });

            } catch (error) {
                console.error("Error actualizando usuario:", error);
                Swal.fire("Error", "Hubo un problema al actualizar.", "error");
            }
        },
    },
};
</script>

<template>
    <Layout>
        <div class="mb-4">
            <img :src="require('@/assets/images/recnet-header.png')" alt="Sesiones de reconstrucción"
                class="img-fluid w-100 rounded shadow-sm"
                style="max-height: 260px; object-fit: cover; object-position: center;" />
        </div>
        <PageHeader />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <div class="row d-flex justify-content-between align-items-center">
                            <div class="col-md-9">
                                <b-form-group label="Buscador por nombre o email:" label-for="searchInput" class="mb-0">
                                    <b-form-input id="searchInput" v-model="searchQuery"
                                        placeholder="Buscar por nombre o email..." />
                                </b-form-group>
                            </div>
                            <div class="col-md-3 d-flex align-items-end">
                                <b-form-group label="Filtrar y descargar reporte" label-for="filterType" class=" mb-0">
                                    <b-dropdown class="ml-3" variant="primary">
                                        <template v-slot:button-content>
                                            {{ filtro }}
                                            <i class="mdi mdi-chevron-down ms-2"></i>
                                        </template>
                                        <b-dropdown-item href="javascript: void(0);"
                                            @click="filtro = 'Todos los usuarios'"
                                            v-if="filtro != 'Todos los usuarios'">Todos los usuarios</b-dropdown-item>
                                        <b-dropdown-item href="javascript: void(0);"
                                            @click="filtro = 'Usuarios Activos'"
                                            v-if="filtro != 'Usuarios Activos'">Usuarios Activos</b-dropdown-item>
                                        <b-dropdown-item href="javascript: void(0);"
                                            @click="filtro = 'Usuarios Inactivos'"
                                            v-if="filtro != 'Usuarios Inactivos'">Usuarios Inactivos</b-dropdown-item>
                                    </b-dropdown>
                                    <b-button variant="info" class="ml-2" @click="openDownloadModal">
                                        <i class="mdi mdi-download"></i>
                                    </b-button>
                                </b-form-group>
                            </div>
                        </div>
                        <table class="table table-centered mb-0 table-nowrap mt-2">
                            <thead class="thead-light">
                                <tr>
                                    <th>Nombre</th>
                                    <th>Telefono</th>
                                    <th>Fecha de Registro</th>
                                    <th>Ver más</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="data in paginatedUsers" :key="data.email">
                                    <td>
                                        {{ data.name }} <br />
                                        {{ data.email }}
                                    </td>
                                    <td>{{ data.phone }}</td>
                                    <td>{{ data.creation_formatted }}</td>
                                    <td>
                                        <button type="button" class="btn btn-success" @click="verPerfil(data)">
                                            <i class="mdi mdi-eye ms-2"></i>
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="paginatedUsers.length === 0">
                                    <td colspan="5" class="text-center text-muted py-4">
                                        No hay usuarios que coincidan con tu búsqueda.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-center my-3">
                            <b-pagination v-model="currentPage" :total-rows="filteredUsers.length" :per-page="perPage"
                                align="center" size="md" pills></b-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <b-modal v-model="showDownloadModal" title="Descargar usuarios" centered>
            <div>
                <b-form-group style="font-weight: bold" label="¿Qué usuarios deseas descargar?">
                    <b-form-radio-group v-model="downloadOption" name="downloadOption">
                        <b-form-radio value="todos">Todos los usuarios</b-form-radio>
                        <p></p>
                        <b-form-radio value="activos">Solo usuarios activos</b-form-radio>
                        <p></p>
                        <b-form-radio value="inactivos">Solo usuarios inactivos</b-form-radio>
                    </b-form-radio-group>
                </b-form-group>
            </div>
            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="showDownloadModal = false">Cancelar</b-button>
                <b-button variant="success" @click="handleDownload">Descargar</b-button>
            </template>
        </b-modal>

        <b-modal v-model="showUser" id="edit-user-modal" size="lg" centered @hidden="cancelarEdicion">
            <template #modal-header="{ close }">
                <div class="d-flex justify-content-between align-items-center w-100" v-if="userSelected">
                    <div>
                        <h4 class="mb-0">{{ userSelected.name }}</h4>
                        <small class="text-muted">{{ userSelected.email }}</small>
                    </div>
                    <b-button size="sm" variant="outline-secondary" @click="close()">
                        &times;
                    </b-button>
                </div>
            </template>

            <div v-if="userSelected">
                <div class="info-card">
                    <div class="card-header">
                        <h5>Información del Usuario</h5>
                        <div v-if="!isEditing">
                            <b-button v-if="puedeEditar" variant="primary" size="sm"
                                @click="activarEdicion">Editar</b-button>
                        </div>
                        <div v-else>
                            <b-button variant="secondary" size="sm" @click="cancelarEdicion"
                                class="mr-2">Cancelar</b-button>
                            <b-button variant="success" size="sm" @click="guardarCambios">Guardar</b-button>
                        </div>
                    </div>
                    <div class="card-body">
                        <b-row>
                            <b-col sm="6">
                                <div class="data-point">
                                    <strong>Nombre</strong>
                                    <p v-if="!isEditing" class="mb-0">{{ userSelected.name }}</p>
                                    <b-form-input v-else v-model="editableUser.name" size="sm"></b-form-input>
                                </div>
                                <div class="data-point">
                                    <strong>Teléfono</strong>
                                    <p v-if="!isEditing" class="mb-0">{{ userSelected.phone }}</p>
                                    <b-form-input v-else v-model="editableUser.phone" size="sm"></b-form-input>
                                </div>

                                <div class="data-point">
                                    <strong>Fecha de creación</strong>
                                    <p class="mb-0">{{ userSelected.creation_formatted }}</p>
                                </div>
                                <div class="data-point">
                                    <strong>Suscripción Activa</strong>
                                    <p :class="`text-${isSubscriptionActive.variant}`" class="font-weight-bold">{{
                                        isSubscriptionActive.text }}</p>
                                </div>
                            </b-col>
                            <b-col sm="6">
                                <div class="data-point">
                                    <strong>Correo Electrónico</strong>
                                    <p class="mb-0">{{ userSelected.email }}</p>
                                </div>
                                <div class="data-point">
                                    <strong>Ciudad</strong>
                                    <p v-if="!isEditing" class="mb-0">{{ userSelected.city }}</p>
                                    <b-form-input v-else v-model="editableUser.city" size="sm"></b-form-input>
                                </div>
                                <div class="data-point">
                                    <strong>Tipo</strong>
                                    <p class="mb-0">{{ userType }}</p>
                                </div>
                                <div class="data-point">
                                    <strong>Código de Referido</strong>
                                    <p class="mb-0">{{ userSelected.referral_code }}</p>
                                </div>
                            </b-col>
                        </b-row>

                        <hr v-if="isEditing">
                        <b-row v-if="isEditing">
                            <b-col sm="12">
                                <div class="data-point">
                                    <strong>Referido por (código)</strong>
                                    <b-form-input v-model="editableUser.referred_by"
                                        placeholder="Ingresa el código del usuario que lo refirió"></b-form-input>
                                </div>
                            </b-col>
                        </b-row>
                        <div v-else class="mt-3">
                            <strong>Referido por:</strong> {{ referredByName }}
                        </div>
                    </div>
                </div>

                <div class="info-card">
                    <div class="card-header">
                        <h5>Documentos</h5>
                    </div>
                    <ul class="document-list">
                        <li v-for="doc in documentList" :key="doc.label" class="document-item">
                            <span class="label">{{ doc.label }}</span>
                            <a v-if="doc.url" :href="doc.url" target="_blank" class="text-success">Ver Documento ✅</a>
                            <span v-else-if="doc.value" class="text-body">{{ doc.value || 'No especificado' }}</span>
                            <span v-else class="text-muted">No subido ⚠️</span>
                        </li>
                    </ul>
                </div>

                <div class="info-card">
                    <div class="card-header">
                        <h5>Referidos del Usuario</h5>
                    </div>
                    <div class="card-body">
                        <b-nav pills small class="mb-3">
                            <b-nav-item :active="filtro_referidos === 'Activos'"
                                @click="filtro_referidos = 'Activos'">Activos</b-nav-item>
                            <b-nav-item :active="filtro_referidos === 'Inactivos'"
                                @click="filtro_referidos = 'Inactivos'">Inactivos</b-nav-item>
                        </b-nav>
                        <b-table striped hover responsive :items="filteredReferrals" :fields="referralFields">
                            <template #cell(name)="data">
                                {{ data.item.name }} <br />
                                <small class="text-muted">{{ data.item.email }}</small>
                            </template>
                        </b-table>
                        <div v-if="filteredReferrals.length === 0" class="text-center text-muted mt-3">
                            No hay referidos en esta categoría.
                        </div>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <b-button variant="secondary" @click="showUser = false">Cerrar</b-button>
            </template>
        </b-modal>
    </Layout>
</template>

<style scoped>
.info-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    background-color: #fff;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f8f9fa;
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid #e9ecef;
}

.card-header h5 {
    margin-bottom: 0;
}

.card-body {
    padding: 1.25rem;
}

.data-point {
    margin-bottom: 1rem;
}

.data-point p {
    color: #212529;
}

.document-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.document-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.25rem;
}

.document-item .label {
    font-weight: bold;
}

.document-item:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
}
</style>